import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
// A prerequisite (or an as-yet unclassified signature) must never start gasless expiry.
export const useDepositSignatureStore = create(persist(set => ({
    signatures: {},
    setDepositSignature: (id, validBefore) => set(state => ({
        signatures: { ...state.signatures, [id]: { validBefore } },
    })),
    removeDepositSignature: id => set(state => {
        if (!state.signatures[id])
            return state;
        const { [id]: removed, ...signatures } = state.signatures;
        return { signatures };
    }),
}), { name: 'depositSignatures', storage: createJSONStorage(() => localStorage) }));
function savedRecords(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return {};
    return value;
}
function transactionReceipt(value) {
    const saved = savedRecords(value);
    if (typeof saved.hash !== 'string' || !saved.hash.trim())
        return undefined;
    return { hash: saved.hash, timestamp: savedTimestamp(saved.timestamp) };
}
function savedTimestamp(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
function savedAtomicBatch(value) {
    const batch = savedRecords(value);
    if (typeof batch.id !== 'string' || !batch.id.trim() || batch.id.length > 8194)
        return undefined;
    return { id: batch.id };
}
function transactionReceipts(value) {
    const saved = savedRecords(value);
    const swapTransactions = {};
    for (const [id, value] of Object.entries(savedRecords(saved.swapTransactions))) {
        const entry = savedRecords(value);
        const receipt = transactionReceipt(value);
        const kind = entry.kind === 'wallet-transfer' || entry.kind === 'frontend-swap'
            || entry.kind === 'atomic-batch' || entry.kind === 'gasless-deposit' ? entry.kind : undefined;
        if (receipt) {
            swapTransactions[id] = { kind: kind ?? 'transaction', ...receipt };
        }
        else if (entry.providerSubmissionAccepted === true) {
            swapTransactions[id] = {
                kind: kind ?? 'wallet-transfer',
                providerSubmissionAccepted: true,
                timestamp: savedTimestamp(entry.timestamp),
            };
        }
        else if (kind) {
            swapTransactions[id] = { kind };
        }
        if (kind === 'atomic-batch' && swapTransactions[id]) {
            if (entry.atomicBatchFailure === 'reverted' || entry.atomicBatchFailure === 'not_submitted') {
                swapTransactions[id] = { kind, ...(receipt ?? {}),
                    timestamp: savedTimestamp(entry.timestamp), atomicBatchFailure: entry.atomicBatchFailure };
            }
            else if (!receipt && entry.providerSubmissionAccepted === true) {
                const batch = savedAtomicBatch(entry.atomicBatch);
                if (batch)
                    swapTransactions[id].atomicBatch = batch;
            }
        }
    }
    const stepTransactions = {};
    for (const [id, steps] of Object.entries(savedRecords(saved.stepTransactions))) {
        const receipts = {};
        for (const [step, value] of Object.entries(savedRecords(steps))) {
            const receipt = transactionReceipt(value);
            const explorerUrl = savedRecords(value).explorerUrl;
            if (receipt && typeof explorerUrl === 'string')
                receipts[step] = { ...receipt, explorerUrl };
        }
        if (Object.keys(receipts).length)
            stepTransactions[id] = receipts;
    }
    const pendingSubmissions = {};
    for (const [id, pending] of Object.entries(savedRecords(saved.pendingSubmissions))) {
        if (pending === true)
            pendingSubmissions[id] = true;
    }
    return { swapTransactions, stepTransactions, pendingSubmissions };
}
function gaslessAuthorizationReceipts(persisted) {
    const authorizations = {};
    for (const [id, value] of Object.entries(savedRecords(savedRecords(persisted).authorizations))) {
        const saved = savedRecords(value);
        if (typeof saved.validBefore !== 'number' || !Number.isFinite(saved.validBefore))
            continue;
        authorizations[id] = {
            ...(saved.kind === 'gasless' ? { kind: 'gasless' } : {}),
            validBefore: saved.validBefore,
        };
    }
    return { authorizations };
}
export const useSwapTransactionStore = create()(persist(set => ({
    swapTransactions: {},
    stepTransactions: {},
    pendingSubmissions: {},
    setStepTransaction: (id, step, hash, explorerUrl) => {
        if (!hash.trim())
            return;
        set(state => ({
            stepTransactions: {
                ...state.stepTransactions,
                [id]: {
                    ...state.stepTransactions[id],
                    [step]: {
                        hash,
                        explorerUrl,
                        timestamp: state.stepTransactions[id]?.[step]?.hash === hash
                            ? state.stepTransactions[id][step].timestamp : Date.now(),
                    },
                },
            },
        }));
    },
    markSubmissionPending: id => set(state => ({
        pendingSubmissions: { ...state.pendingSubmissions, [id]: true },
    })),
    clearPendingSubmission: id => set(state => {
        const { [id]: removed, ...pendingSubmissions } = state.pendingSubmissions;
        return { pendingSubmissions };
    }),
    setProviderSubmissionAccepted: id => set(state => {
        const current = state.swapTransactions[id];
        const { [id]: removed, ...pendingSubmissions } = state.pendingSubmissions;
        return {
            swapTransactions: {
                ...state.swapTransactions,
                [id]: {
                    ...current,
                    kind: current?.kind ?? 'wallet-transfer',
                    providerSubmissionAccepted: true,
                    timestamp: current?.timestamp ?? Date.now(),
                },
            },
            pendingSubmissions,
        };
    }),
    setAtomicBatchSubmission: (id, batch) => set(state => {
        const current = state.swapTransactions[id];
        if (current?.hash)
            return state;
        const { [id]: removed, ...pendingSubmissions } = state.pendingSubmissions;
        return {
            swapTransactions: { ...state.swapTransactions, [id]: {
                    kind: 'atomic-batch', providerSubmissionAccepted: true, atomicBatch: batch, timestamp: Date.now(),
                } },
            pendingSubmissions,
        };
    }),
    setSwapTransactionKind: (id, kind) => set(state => {
        const current = state.swapTransactions[id];
        if (current?.kind === kind)
            return state;
        return { swapTransactions: { ...state.swapTransactions, [id]: { ...current, kind } } };
    }),
    setSwapTransaction: (id, hash, kind) => {
        if (!hash.trim())
            return;
        set(state => {
            const current = state.swapTransactions[id];
            const { [id]: removed, ...pendingSubmissions } = state.pendingSubmissions;
            return {
                swapTransactions: {
                    ...state.swapTransactions,
                    [id]: {
                        kind: kind ?? current?.kind ?? 'transaction',
                        hash,
                        timestamp: current?.hash === hash ? current.timestamp : Date.now(),
                    },
                },
                pendingSubmissions,
            };
        });
    },
    removeSwapTransaction: id => set(state => {
        const { [id]: removed, ...swapTransactions } = state.swapTransactions;
        return { swapTransactions };
    }),
}), {
    name: 'swapTransactions',
    version: 1,
    storage: createJSONStorage(() => localStorage),
    partialize: transactionReceipts,
    migrate: transactionReceipts,
    merge: (persisted, current) => ({ ...current, ...transactionReceipts(persisted) }),
}));
export const useGaslessAuthorizationStore = create()(persist((set) => ({
    authorizations: {},
    setGaslessAuthorization: (Id, validBefore) => {
        set((state) => ({
            authorizations: {
                ...state.authorizations,
                [Id]: { kind: 'gasless', validBefore },
            },
        }));
    },
    removeGaslessAuthorization: (Id) => {
        set((state) => {
            const { [Id]: _removed, ...remaining } = state.authorizations;
            return { authorizations: remaining };
        });
    },
}), {
    name: 'gaslessAuthorizations',
    storage: createJSONStorage(() => localStorage),
    partialize: gaslessAuthorizationReceipts,
    merge: (persisted, current) => ({ ...current, ...gaslessAuthorizationReceipts(persisted) }),
}));
export const useSwapDepositHintClicked = create(persist((set, get) => ({
    swapTransactions: {},
    setSwapDepositHintClicked: (Id) => {
        set((state) => {
            const txForSwap = {
                ...state.swapTransactions,
                [Id]: true
            };
            return { swapTransactions: txForSwap };
        });
    },
}), {
    name: 'swapDepositHintClicked',
    storage: createJSONStorage(() => sessionStorage),
}));
